import { useCallback, useEffect, useRef, useState } from 'react';
import { FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  downloadLoanDataExport,
  getLoanDataExportDownload,
  getLoanDataExportHistory
} from '@/services/loanDataExportService';
import type { LoanDataExportHistoryItem, LoanDataExportHistoryPage } from '@/types/loan-data-export';
import { cn } from '@/lib/utils';

interface ExportHistoryTableProps {
  refreshKey?: number;
}

const PAGE_SIZE = 25;

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(value));
}

function formatBytes(value: number | null): string {
  if (!value) {
    return '—';
  }
  if (value < 1024 * 1024) {
    return `${(value / 1024).toFixed(0)} KB`;
  }
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function requesterName(item: LoanDataExportHistoryItem): string {
  const fullName = [item.requester?.first_name, item.requester?.last_name].filter(Boolean).join(' ');
  return fullName || item.requester?.email || 'Unknown user';
}

function statusClass(status: LoanDataExportHistoryItem['status']): string {
  if (status === 'complete') {
    return 'bg-success/10 text-success';
  }
  if (status === 'failed') {
    return 'bg-destructive/10 text-destructive';
  }
  return 'bg-warning/10 text-warning-strong';
}

export function ExportHistoryTable({ refreshKey = 0 }: ExportHistoryTableProps) {
  const [page, setPage] = useState(1);
  const [history, setHistory] = useState<LoanDataExportHistoryPage>({
    items: [],
    page: 1,
    pageSize: PAGE_SIZE,
    totalCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setHistory(await getLoanDataExportHistory(page, PAGE_SIZE));
    } catch (historyError) {
      setError(historyError instanceof Error ? historyError.message : 'Unable to load export history');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory, refreshKey]);

  const handleDownload = async (item: LoanDataExportHistoryItem) => {
    if (!item.file_name) {
      return;
    }
    setDownloadingId(item.id);
    try {
      const result = await getLoanDataExportDownload(item.id);
      // Leaving the page (or another user signing in) stops the download.
      if (!mountedRef.current) {
        return;
      }
      await downloadLoanDataExport(result.download.signedUrl, item.file_name, {
        shouldContinue: () => mountedRef.current
      });
    } catch (downloadError) {
      if (mountedRef.current) {
        toast.error(downloadError instanceof Error ? downloadError.message : 'Download failed');
      }
    } finally {
      if (mountedRef.current) {
        setDownloadingId(null);
      }
    }
  };

  const totalPages = Math.max(1, Math.ceil(history.totalCount / PAGE_SIZE));

  return (
    <Card className="overflow-hidden rounded-xl shadow-none">
      <div className="border-b px-4 py-3">
        <h2 className="text-base font-semibold">Export history</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Files stay in private storage and download through short-lived secure links.
        </p>
      </div>

      <Table
        className="min-w-[900px] text-[13px] [line-height:normal]"
        containerProps={{ className: 'overflow-x-auto' }}
      >
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="h-10 px-4 text-xs font-medium text-muted-foreground">Export</TableHead>
            <TableHead className="h-10 px-3 text-xs font-medium text-muted-foreground">Status</TableHead>
            <TableHead className="h-10 px-3 text-right text-xs font-medium text-muted-foreground">Loans</TableHead>
            <TableHead className="h-10 px-3 text-right text-xs font-medium text-muted-foreground">Missing values</TableHead>
            <TableHead className="h-10 px-3 text-xs font-medium text-muted-foreground">Requested by</TableHead>
            <TableHead className="h-10 px-3 text-xs font-medium text-muted-foreground">Created</TableHead>
            <TableHead className="h-10 px-4 text-right text-xs font-medium text-muted-foreground">Size</TableHead>
            <TableHead className="h-10 px-4 text-right"><span className="sr-only">Download</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && history.items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="h-40 text-center text-muted-foreground">
                <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                Loading export history…
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={8} className="h-40 text-center text-destructive">{error}</TableCell>
            </TableRow>
          ) : history.items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="h-40 text-center text-muted-foreground">
                <FileSpreadsheet className="mx-auto mb-2 h-8 w-8 opacity-50" />
                No exports yet. Generated Excel files appear here.
              </TableCell>
            </TableRow>
          ) : history.items.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="px-4 py-[11px]">
                <div className="font-semibold">{item.template_name}</div>
                <div className="max-w-72 truncate text-xs text-muted-foreground" title={item.failure_message || item.file_name || undefined}>
                  {item.file_name || `Version ${item.template_version}`}
                </div>
              </TableCell>
              <TableCell className="px-2.5 py-[11px]">
                <span className={cn('inline-block rounded-full px-[9px] py-[3px] text-xs font-semibold capitalize', statusClass(item.status))}>
                  {item.status}
                </span>
              </TableCell>
              <TableCell className="px-2.5 py-[11px] text-right tabular-nums">{item.loan_count.toLocaleString()}</TableCell>
              <TableCell className="px-2.5 py-[11px] text-right tabular-nums text-muted-foreground">{item.warning_count > 0 ? item.warning_count.toLocaleString() : '—'}</TableCell>
              <TableCell className="px-2.5 py-[11px]">{requesterName(item)}</TableCell>
              <TableCell className="whitespace-nowrap px-2.5 py-[11px] text-muted-foreground">{formatDateTime(item.created_at)}</TableCell>
              <TableCell className="px-4 py-[11px] text-right tabular-nums text-muted-foreground">{formatBytes(item.file_size_bytes)}</TableCell>
              <TableCell className="px-4 py-[11px] text-right">
                <Button
                  type="button"
                  variant="outline"
                  className="shadow-none"
                  disabled={item.status !== 'complete' || !item.file_name || downloadingId === item.id}
                  onClick={() => void handleDownload(item)}
                >
                  {downloadingId === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  Download
                  <span className="sr-only"> {item.file_name}</span>
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {history.totalCount > PAGE_SIZE ? (
        <div className="flex items-center justify-between gap-3 border-t px-4 py-2">
          <span className="text-xs text-muted-foreground">
            {history.totalCount.toLocaleString()} exports · Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" className="shadow-none" disabled={page <= 1 || loading} onClick={() => setPage((current) => current - 1)}>
              Previous
            </Button>
            <Button type="button" size="sm" variant="outline" className="shadow-none" disabled={page >= totalPages || loading} onClick={() => setPage((current) => current + 1)}>
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
